/**
 * Hook personalizado para gestionar la actualización de ubicación del usuario.
 * Similar a la implementación en amarsi-app (AppStack.js).
 * Maneja la actualización periódica de ubicación en la tabla user_geo.
 * 
 * @param {Object} user - Usuario autenticado
 * @returns {Object} - Estado y funciones de ubicación
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// Intervalo mínimo entre actualizaciones (30 segundos)
const MIN_UPDATE_INTERVAL = 30000;

export const useUserLocation = (user) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  
  // Ref para trackear la última vez que se envió la ubicación
  const lastSentRef = useRef(0);

  /**
   * Actualiza la ubicación del usuario en la tabla user_geo
   * Usa el formato POINT(lng lat) compatible con PostGIS
   * Implementa throttling para evitar actualizaciones demasiado frecuentes
   */
  const updateGeoLocation = useCallback(async (latitude, longitude) => {
    const now = Date.now();

    // Verificar intervalo mínimo entre actualizaciones
    if (now - lastSentRef.current < MIN_UPDATE_INTERVAL) {
      console.log('[useUserLocation] Ubicación actualizada recientemente, omitiendo');
      return;
    }

    lastSentRef.current = now;

    if (!user?.id) {
      console.log('[useUserLocation] Sin sesión de usuario');
      return;
    }

    try {
      console.log('[useUserLocation] Actualizando ubicación en servidor:', { latitude, longitude });

      const { data, error } = await supabase.rpc('update_user_geo', {
        lat_param: latitude,
        lng_param: longitude
      });

      if (error) {
        console.error('[useUserLocation] ERROR actualizando ubicación:', error);
        setError('No se pudo actualizar la ubicación en el servidor');
      } else {
        console.log('[useUserLocation] Ubicación actualizada exitosamente');
      }
    } catch (e) {
      console.error('[useUserLocation] Excepción actualizando ubicación:', e);
      setError('Error al actualizar ubicación');
    }
  }, [user?.id]);

  /**
   * Obtiene las coordenadas actuales del dispositivo
   * Usa la Geolocation API del navegador
   * Muestra alerta si los permisos son denegados
   */
  const updateCoordinates = useCallback(async () => {
    try {
      if (!navigator.geolocation) {
        setError('Geolocalización no soportada por este navegador');
        return;
      }

      setLoading(true);
      setError(null);

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      });

      const { latitude, longitude } = position.coords;
      
      setLocation({ lat: latitude, lng: longitude, accuracy: position.coords.accuracy });
      console.log('[useUserLocation] Coordenadas obtenidas:', { latitude, longitude });

      // Actualizar ubicación en el servidor
      await updateGeoLocation(latitude, longitude);

      setLoading(false);
    } catch (error) {
      console.error('[useUserLocation] Error obteniendo ubicación:', error);
      
      let errorMessage = 'Error al obtener ubicación';
      if (error.code === error.PERMISSION_DENIED) {
        errorMessage = 'Permiso de ubicación denegado. Por favor, habilita la ubicación en tu navegador.';
        // Mostrar alerta para solicitar permisos
        setShowPermissionAlert(true);
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        errorMessage = 'Información de ubicación no disponible.';
      } else if (error.code === error.TIMEOUT) {
        errorMessage = 'Tiempo de espera agotado al obtener ubicación.';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  }, [updateGeoLocation]);

  /**
   * Solicita permisos de geolocalización manualmente
   * Útil cuando el usuario denegó los permisos inicialmente
   */
  const requestLocationPermission = useCallback(async () => {
    setShowPermissionAlert(false);
    await updateCoordinates();
  }, [updateCoordinates]);

  /**
   * Efecto que solicita ubicación al montar el componente
   * Y configura actualización periódica
   */
  useEffect(() => {
    if (!user?.id) {
      console.log('[useUserLocation] Sin usuario, omitiendo geolocalización');
      return;
    }

    console.log('[useUserLocation] Iniciando geolocalización para usuario:', user.id);

    // Obtener ubicación inicial
    updateCoordinates();

    // Configurar actualización periódica (cada 5 minutos)
    const intervalId = setInterval(() => {
      console.log('[useUserLocation] Actualización periódica de ubicación');
      updateCoordinates();
    }, 5 * 60 * 1000); // 5 minutos

    // Cleanup al desmontar
    return () => {
      console.log('[useUserLocation] Limpiando intervalo de geolocalización');
      clearInterval(intervalId);
    };
  }, [user?.id, updateCoordinates]);

  return {
    location,
    error,
    loading,
    showPermissionAlert,
    setShowPermissionAlert,
    requestLocationPermission,
    updateCoordinates,
    updateGeoLocation
  };
};
